import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Plus, Edit2, Trash2, Upload, Search, X, Users, CheckCircle2, Clock, UserPlus, Eye, Mail, Utensils, Leaf, MessageCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyParty = () => ({
  party_name: '',
  primary_guest: { first_name: '', last_name: '', phone: '', email: '' },
  additional_guests: [],
  plus_one_type: 'none',
  plus_one_name: '',
  meal_options: [],
  admin_notes: '',
});

/**
 * AdminGuestListManager — EverVow-aligned party CMS.
 *
 * Manages the couple's invitation list. Each party has one lookup phone,
 * optional additional named guests, and a plus-one policy (none/named/open)
 * that drives the guest-facing RSVP form.
 */
const AdminGuestListManager = ({ authHeader }) => {
  const [parties, setParties] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // full party object or null
  const [viewingRsvp, setViewingRsvp] = useState(null); // party with rsvp we're inspecting
  const [showCreate, setShowCreate] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [defaultMeals, setDefaultMeals] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    // Fetch each endpoint independently so a stats regression can never
    // hide the party list — the two calls have unrelated failure modes.
    try {
      const p = await axios.get(`${API}/admin/parties`, { headers: authHeader() });
      setParties(p.data.parties || []);
      setDefaultMeals(p.data.meal_options_default || []);
    } catch (err) {
      setError('Could not load the guest list. Please refresh.');
    }
    try {
      const s = await axios.get(`${API}/admin/parties/stats`, { headers: authHeader() });
      setStats(s.data);
    } catch (err) {
      // Stats are decorative; keep parties visible even if this fails.
      setStats(null);
    }
    setLoading(false);
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const remove = async (party) => {
    const label = party.party_name || `${party.primary_guest.first_name} ${party.primary_guest.last_name || ''}`;
    if (!window.confirm(`Delete party "${label}" and its RSVP?`)) return;
    try {
      await axios.delete(`${API}/admin/parties/${party.id}`, { headers: authHeader() });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed.');
    }
  };

  const filtered = search.trim()
    ? parties.filter((p) => {
        const q = search.toLowerCase();
        const hay = [
          p.party_name, p.primary_guest?.first_name, p.primary_guest?.last_name,
          p.primary_guest?.phone, p.primary_guest?.email, p.plus_one_name,
          ...(p.additional_guests || []).flatMap((a) => [a.first_name, a.last_name]),
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      })
    : parties;

  const importCSV = async (file) => {
    setImporting(true);
    setImportResult(null);
    try {
      const text = await file.text();
      const res = await axios.post(`${API}/admin/parties/import`, text, {
        headers: { ...authHeader(), 'Content-Type': 'text/csv' },
      });
      setImportResult(res.data);
      await load();
    } catch (err) {
      setImportResult({ errors: [{ row: 0, error: err.response?.data?.detail || 'Import failed' }], created: 0, skipped: 0 });
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-10 flex items-center justify-center gap-3 text-[#5A4A4A]">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading guest list…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-guest-list-panel">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: Users,        label: 'Parties',           value: stats.total_parties },
            { icon: CheckCircle2, label: 'Responded',         value: stats.responded_parties },
            { icon: Clock,        label: 'Pending',           value: stats.pending_parties },
            { icon: UserPlus,     label: 'Seats attending',   value: stats.seats_attending },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-1 text-[#C8A951]"><Icon className="w-4 h-4" /><span className="text-xs uppercase tracking-widest">{label}</span></div>
              <p className="font-serif text-2xl text-[#3B2F2F]">{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-[#8A7A7A] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search party, guest, phone, email…"
            className="w-full pl-9 pr-9 py-2 rounded-full border border-[#E5E0D8] bg-white text-sm text-[#3B2F2F] focus:outline-none focus:border-[#C8A951]"
            data-testid="admin-party-search"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#8A7A7A] hover:text-[#3B2F2F]">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white border border-[#E5E0D8] text-sm text-[#3B2F2F] hover:bg-[#F8F5EF] cursor-pointer">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Import CSV
            <input type="file" accept=".csv" className="hidden"
              onChange={(e) => e.target.files?.[0] && importCSV(e.target.files[0])}
              data-testid="admin-party-import-input"
            />
          </label>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3B2F2F] text-white text-sm hover:bg-[#2A2020]"
            data-testid="admin-party-add"
          >
            <Plus className="w-4 h-4" /> Add party
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}

      {importResult && (
        <div className="bg-[#FCFBF7] border border-[#E5E0D8] rounded-lg p-3 text-sm text-[#3B2F2F]">
          <div><strong>Import:</strong> {importResult.created} created, {importResult.skipped} skipped
            {importResult.errors?.length ? `, ${importResult.errors.length} errors` : ''}.
          </div>
          {importResult.errors?.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-xs text-red-600 space-y-0.5">
              {importResult.errors.slice(0, 10).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
            </ul>
          )}
          <button onClick={() => setImportResult(null)} className="text-xs underline mt-2">Dismiss</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F5EF] text-[#3B2F2F]">
              <tr>
                <th className="text-left p-3">Party</th>
                <th className="text-left p-3">Phone</th>
                <th className="text-left p-3">Plus-One</th>
                <th className="text-left p-3">Invited Seats</th>
                <th className="text-left p-3">RSVP</th>
                <th className="text-left p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="6" className="p-6 text-center text-[#5A4A4A] italic">
                  {search ? 'No parties match your search.' : 'No parties yet. Add one or import a CSV to get started.'}
                </td></tr>
              )}
              {filtered.map((p) => {
                const primaryName = `${p.primary_guest.first_name} ${p.primary_guest.last_name || ''}`.trim();
                const label = p.party_name || primaryName;
                const members = 1 + (p.additional_guests?.length || 0) + (p.plus_one_type !== 'none' ? 1 : 0);
                const status = p.rsvp
                  ? (p.rsvp.primary_response.attending === 'yes' ? 'Attending' : 'Declined')
                  : 'Pending';
                return (
                  <tr key={p.id} className="border-t border-[#E5E0D8]" data-testid={`admin-party-row-${p.id}`}>
                    <td className="p-3 text-[#3B2F2F] font-medium">
                      <div>{label}</div>
                      <div className="text-xs text-[#8A7A7A]">{primaryName}</div>
                    </td>
                    <td className="p-3 text-[#5A4A4A]">{p.primary_guest.phone}</td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${
                        p.plus_one_type === 'none' ? 'bg-gray-100 text-gray-700'
                        : p.plus_one_type === 'named' ? 'bg-[#C8A951]/20 text-[#7A5A00]'
                        : 'bg-blue-100 text-blue-700'
                      }`}>{p.plus_one_type === 'named' ? `Named: ${p.plus_one_name}` : p.plus_one_type}</span>
                    </td>
                    <td className="p-3 text-[#5A4A4A]">{members}</td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${
                        status === 'Attending' ? 'bg-green-100 text-green-700'
                        : status === 'Declined' ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                      }`} data-testid={`admin-party-status-${p.id}`}>{status}</span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        {p.rsvp && (
                          <button
                            onClick={() => setViewingRsvp(p)}
                            className="p-1.5 rounded hover:bg-[#F8F5EF] text-[#C8A951]"
                            title="View submitted RSVP"
                            data-testid={`admin-party-view-rsvp-${p.id}`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={() => setEditing(p)} className="p-1.5 rounded hover:bg-[#F8F5EF] text-[#3B2F2F]" title="Edit party" data-testid={`admin-party-edit-${p.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                        <button onClick={() => remove(p)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete party" data-testid={`admin-party-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* CSV help */}
      <details className="bg-white rounded-xl shadow-sm p-4 text-sm text-[#5A4A4A]">
        <summary className="cursor-pointer text-[#3B2F2F] font-medium">CSV import format</summary>
        <div className="mt-2 space-y-1">
          <p>One row per party. Header row required. Columns (any order, case-insensitive):</p>
          <code className="block bg-[#F8F5EF] p-2 rounded text-xs whitespace-pre-wrap">
            party_name, first_name, last_name, phone, email, plus_one_type (none|named|open), plus_one_name, additional_guests, meal_options, admin_notes
          </code>
          <p><code className="text-xs">additional_guests</code> and <code className="text-xs">meal_options</code> use <strong>semicolons</strong> to separate values (e.g. <code>"Jane Doe; John Doe"</code>).</p>
          <p>Rows whose phone already exists in the list are <strong>skipped</strong> — safe to re-import.</p>
        </div>
      </details>

      {(showCreate || editing) && (
        <PartyEditor
          initial={editing || emptyParty()}
          isEdit={!!editing}
          defaultMeals={defaultMeals}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={async () => { setShowCreate(false); setEditing(null); await load(); }}
          authHeader={authHeader}
        />
      )}

      {viewingRsvp && (
        <RSVPDetailsModal
          party={viewingRsvp}
          onClose={() => setViewingRsvp(null)}
        />
      )}
    </div>
  );
};

/**
 * RSVPDetailsModal — read-only view of a party's submitted response.
 *
 * Renders each attendee individually (primary, additional named guests,
 * plus-one) with attendance status, meal choice, dietary restrictions,
 * contact email, message to the couple, and the submitted / updated
 * timestamps. Edit Party remains the tool for invitation setup — this
 * modal never mutates the RSVP.
 */
const RSVPDetailsModal = ({ party, onClose }) => {
  const rsvp = party.rsvp;
  const primaryName = `${party.primary_guest.first_name} ${party.primary_guest.last_name || ''}`.trim();
  const partyLabel = party.party_name || primaryName;

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    } catch { return iso; }
  };

  const StatusPill = ({ attending }) => (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
        attending === 'yes' || attending === true
          ? 'bg-green-100 text-green-700'
          : 'bg-red-100 text-red-700'
      }`}
      data-testid="admin-rsvp-attendee-status"
    >
      {(attending === 'yes' || attending === true) ? 'Attending' : 'Not attending'}
    </span>
  );

  const AttendeeCard = ({ label, badge, attending, meal, dietary, testId }) => (
    <div className="bg-[#F8F5EF] rounded-lg p-4 space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="font-serif text-base text-[#3B2F2F]">{label}</p>
          {badge && <p className="text-[10px] uppercase tracking-widest text-[#8A7A7A]">{badge}</p>}
        </div>
        <StatusPill attending={attending} />
      </div>
      {(attending === 'yes' || attending === true) && (
        <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <div className="flex items-center gap-2 text-[#5A4A4A]">
            <Utensils className="w-3.5 h-3.5 text-[#C8A951]" />
            <span>{meal || <em className="text-[#8A7A7A]">No meal selected</em>}</span>
          </div>
          <div className="flex items-center gap-2 text-[#5A4A4A]">
            <Leaf className="w-3.5 h-3.5 text-[#C8A951]" />
            <span>{dietary || <em className="text-[#8A7A7A]">No dietary notes</em>}</span>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto"
      data-testid="admin-rsvp-details-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8 p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#C8A951]">RSVP Details</p>
            <h3 className="font-serif text-xl text-[#3B2F2F]">{partyLabel}</h3>
            {rsvp && (
              <p className="text-xs text-[#8A7A7A] mt-1">
                Submitted {fmtDate(rsvp.submitted_at)}
                {rsvp.updated_at && rsvp.updated_at !== rsvp.submitted_at && (
                  <> · Updated {fmtDate(rsvp.updated_at)}</>
                )}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[#F8F5EF]" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!rsvp ? (
          <div className="text-center py-8 text-[#5A4A4A]" data-testid="admin-rsvp-details-empty">
            <Clock className="w-10 h-10 text-[#C8A951] mx-auto mb-2 opacity-60" />
            <p className="text-sm">This party hasn't responded yet.</p>
          </div>
        ) : (
          <>
            {/* Primary guest */}
            <AttendeeCard
              label={primaryName}
              badge="Primary invitee"
              attending={rsvp.primary_response.attending}
              meal={rsvp.primary_response.meal}
              dietary={rsvp.primary_response.dietary_restrictions}
              testId="admin-rsvp-detail-primary"
            />

            {/* Additional named guests */}
            {(party.additional_guests || []).map((g, idx) => {
              const resp = rsvp.additional_responses?.find((r) => r.guest_index === idx);
              const name = `${g.first_name} ${g.last_name || ''}`.trim();
              return (
                <AttendeeCard
                  key={idx}
                  label={name}
                  badge="Additional guest"
                  attending={resp?.attending || 'no'}
                  meal={resp?.meal}
                  dietary={resp?.dietary_restrictions}
                  testId={`admin-rsvp-detail-guest-${idx}`}
                />
              );
            })}

            {/* Plus-one — rendered only when the party has one AND the
                submitted RSVP marks them as attending. Presence here
                mirrors EverVow: no separate "will they attend?" prompt
                on the guest side, so this card only appears when the
                invitee brought (or is bringing) their plus-one. */}
            {rsvp.plus_one && rsvp.plus_one.attending && (
              <AttendeeCard
                label={rsvp.plus_one.name || 'Plus-One'}
                badge={party.plus_one_type === 'named' ? 'Named plus-one' : 'Open plus-one'}
                attending={true}
                meal={rsvp.plus_one.meal}
                dietary={rsvp.plus_one.dietary_restrictions}
                testId="admin-rsvp-detail-plus-one"
              />
            )}

            {/* Plus-one NOT brought — surface for clarity when the party
                had a plus-one entitlement and the invitee declined it. */}
            {party.plus_one_type !== 'none' && (!rsvp.plus_one || !rsvp.plus_one.attending) && rsvp.primary_response.attending === 'yes' && (
              <div className="bg-[#FCFBF7] border border-dashed border-[#E5E0D8] rounded-lg p-3 text-xs text-[#8A7A7A]" data-testid="admin-rsvp-detail-plus-one-declined">
                <UserPlus className="w-3.5 h-3.5 inline mr-1 text-[#C8A951]" />
                Plus-one entitlement available ({party.plus_one_type}) — the invitee did not bring one.
              </div>
            )}

            {/* Contact + message */}
            <div className="border-t border-[#E5E0D8] pt-4 space-y-3">
              <div className="flex items-start gap-2 text-sm">
                <Mail className="w-4 h-4 text-[#C8A951] mt-0.5 shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#8A7A7A]">Contact email</div>
                  <div className="text-[#3B2F2F]" data-testid="admin-rsvp-detail-email">
                    {rsvp.contact?.email || <em className="text-[#8A7A7A]">Not provided</em>}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <MessageCircle className="w-4 h-4 text-[#C8A951] mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-[#8A7A7A]">Message to the couple</div>
                  <div className="text-[#3B2F2F] whitespace-pre-wrap" data-testid="admin-rsvp-detail-message">
                    {rsvp.message || <em className="text-[#8A7A7A]">No message left</em>}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <div className="flex justify-end pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full bg-[#3B2F2F] text-white text-sm hover:bg-[#2A2020]"
            data-testid="admin-rsvp-details-close"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const PartyEditor = ({ initial, isEdit, defaultMeals, onClose, onSaved, authHeader }) => {
  const [form, setForm] = useState({
    ...initial,
    meal_options: initial.meal_options?.length ? initial.meal_options : [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const updatePrimary = (field, value) => setForm((f) => ({ ...f, primary_guest: { ...f.primary_guest, [field]: value } }));
  const updateAddl = (i, field, value) => setForm((f) => ({ ...f, additional_guests: f.additional_guests.map((a, idx) => idx === i ? { ...a, [field]: value } : a) }));
  const addAddl = () => setForm((f) => ({ ...f, additional_guests: [...f.additional_guests, { first_name: '', last_name: '' }] }));
  const removeAddl = (i) => setForm((f) => ({ ...f, additional_guests: f.additional_guests.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true); setError('');
    try {
      const payload = {
        party_name: form.party_name || null,
        primary_guest: {
          first_name: form.primary_guest.first_name.trim(),
          last_name:  (form.primary_guest.last_name || '').trim(),
          phone:      form.primary_guest.phone.trim(),
          email:      (form.primary_guest.email || '').trim() || null,
        },
        additional_guests: form.additional_guests
          .filter((a) => a.first_name.trim())
          .map((a) => ({ first_name: a.first_name.trim(), last_name: (a.last_name || '').trim() })),
        plus_one_type: form.plus_one_type || 'none',
        plus_one_name: form.plus_one_type === 'named' ? (form.plus_one_name || '').trim() : null,
        meal_options: form.meal_options.filter(Boolean),
        admin_notes: form.admin_notes || null,
      };
      if (isEdit) {
        await axios.put(`${API}/admin/parties/${initial.id}`, payload, { headers: authHeader() });
      } else {
        await axios.post(`${API}/admin/parties`, payload, { headers: authHeader() });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto" data-testid="admin-party-editor">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8 p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl text-[#3B2F2F]">{isEdit ? 'Edit party' : 'Add party'}</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[#F8F5EF]"><X className="w-4 h-4" /></button>
        </div>
        {error && <div className="p-2 bg-red-50 text-red-700 rounded text-sm">{error}</div>}

        <div>
          <label className="text-xs font-medium text-[#3B2F2F] block mb-1">Party name (optional)</label>
          <input value={form.party_name || ''} onChange={(e) => setForm((f) => ({ ...f, party_name: e.target.value }))} className="wedding-input" placeholder="e.g. The Smith Family" />
        </div>

        <fieldset className="border border-[#E5E0D8] rounded-lg p-3">
          <legend className="text-xs font-medium text-[#3B2F2F] px-1">Primary guest</legend>
          <div className="grid sm:grid-cols-2 gap-2">
            <input placeholder="First name *" value={form.primary_guest.first_name} onChange={(e) => updatePrimary('first_name', e.target.value)} className="wedding-input" data-testid="admin-party-primary-first" />
            <input placeholder="Last name" value={form.primary_guest.last_name || ''} onChange={(e) => updatePrimary('last_name', e.target.value)} className="wedding-input" />
            <input placeholder="Phone * (secondary verification)" value={form.primary_guest.phone} onChange={(e) => updatePrimary('phone', e.target.value)} className="wedding-input" data-testid="admin-party-primary-phone" />
            <input placeholder="Email (optional)" type="email" value={form.primary_guest.email || ''} onChange={(e) => updatePrimary('email', e.target.value)} className="wedding-input" />
          </div>
        </fieldset>

        <fieldset className="border border-[#E5E0D8] rounded-lg p-3">
          <legend className="text-xs font-medium text-[#3B2F2F] px-1">Additional named guests</legend>
          <div className="space-y-2">
            {form.additional_guests.map((a, i) => (
              <div key={i} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-center">
                <input placeholder="First name" value={a.first_name} onChange={(e) => updateAddl(i, 'first_name', e.target.value)} className="wedding-input" />
                <input placeholder="Last name" value={a.last_name || ''} onChange={(e) => updateAddl(i, 'last_name', e.target.value)} className="wedding-input" />
                <button type="button" onClick={() => removeAddl(i)} className="p-2 rounded text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button type="button" onClick={addAddl} className="text-xs text-[#3B2F2F] underline">+ Add guest</button>
          </div>
        </fieldset>

        <fieldset className="border border-[#E5E0D8] rounded-lg p-3">
          <legend className="text-xs font-medium text-[#3B2F2F] px-1">Plus-One</legend>
          <div className="flex gap-2 flex-wrap">
            {['none', 'named', 'open'].map((t) => (
              <button key={t} type="button"
                onClick={() => setForm((f) => ({ ...f, plus_one_type: t }))}
                className={`px-3 py-1.5 rounded-full text-xs ${form.plus_one_type === t ? 'bg-[#3B2F2F] text-white' : 'bg-[#F8F5EF] text-[#3B2F2F]'}`}
                data-testid={`admin-party-plus-one-${t}`}
              >{t}</button>
            ))}
          </div>
          {form.plus_one_type === 'named' && (
            <input placeholder="Approved plus-one name" value={form.plus_one_name || ''} onChange={(e) => setForm((f) => ({ ...f, plus_one_name: e.target.value }))} className="wedding-input mt-2" data-testid="admin-party-plus-one-name" />
          )}
        </fieldset>

        <div>
          <label className="text-xs font-medium text-[#3B2F2F] block mb-1">Meal options (leave blank to use default)</label>
          <input
            value={form.meal_options.join(', ')}
            onChange={(e) => setForm((f) => ({ ...f, meal_options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) }))}
            className="wedding-input"
            placeholder={defaultMeals.join(', ')}
          />
        </div>

        <div>
          <label className="text-xs font-medium text-[#3B2F2F] block mb-1">Admin notes</label>
          <textarea rows="2" value={form.admin_notes || ''} onChange={(e) => setForm((f) => ({ ...f, admin_notes: e.target.value }))} className="wedding-input resize-none" />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full bg-white border border-[#E5E0D8] text-[#3B2F2F] text-sm">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="px-4 py-2 rounded-full bg-[#3B2F2F] text-white text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="admin-party-save">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {isEdit ? 'Save changes' : 'Create party'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminGuestListManager;
